import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPortfolios } from '../utils/api';

export default function PortfolioPage() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadPortfolios = async () => {
            const data = await getPortfolios();
            if (data) {
                setProjects(data.map(p => ({
                    id: p.id,
                    slug: p.slug,
                    title: p.title.rendered,
                    tag: p.meta?.tag || 'PROJECT',
                    category: p.meta?.category || '',
                    image: p._embedded?.['wp:featuredmedia']?.[0]?.source_url || ''
                })));
            }
            setLoading(false);
        };
        loadPortfolios();
    }, []);

    return (
        <div className="pt-16 min-h-screen">
            {/* Header */}
            <section className="py-20 px-6 sm:px-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <p className="section-tag inline-block">PORTFOLIO</p>
                    <h1 className="text-5xl md:text-7xl font-black text-nocturne-text leading-tight mb-6 mt-4 uppercase">
                        Digital <span className="gradient-text">Mastery</span>
                    </h1>
                    <p className="text-nocturne-muted text-lg max-w-2xl mx-auto leading-relaxed">
                        A curated selection of high-performance digital architectures and enterprise-grade interfaces engineered for global scale.
                    </p>
                </div>
            </section>

            {/* Portfolio Grid */}
            <section className="py-12 px-6 sm:px-8">
                <div className="max-w-7xl mx-auto">
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="w-12 h-12 border-4 border-nocturne-purple border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : projects.length > 0 ? (
                        <div className="grid md:grid-cols-2 gap-8">
                            {projects.map((p) => (
                                <Link to={`/portfolio/${p.slug}`} key={p.id} className="group cursor-pointer">
                                    <div className="card border-nocturne-purple/20 bg-nocturne-card overflow-hidden group-hover:border-nocturne-purple/60 transition-all">
                                        <div className="relative aspect-video overflow-hidden">
                                            {p.image ? (
                                                <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                            ) : (
                                                <div className="w-full h-full bg-nocturne-bg flex items-center justify-center text-4xl opacity-20">🏗️</div>
                                            )}
                                            <div className="absolute top-4 left-4">
                                                <span className="bg-nocturne-bg/80 backdrop-blur-md border border-nocturne-border px-3 py-1 rounded-full text-[10px] font-bold text-nocturne-purple-light uppercase tracking-widest">
                                                    {p.tag}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-8">
                                            <h3 className="text-2xl font-black text-nocturne-text mb-2 uppercase group-hover:text-nocturne-purple-light transition-colors">{p.title}</h3>
                                            <p className="text-nocturne-muted text-sm">{p.category}</p>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20 text-nocturne-muted border border-dashed border-nocturne-border rounded-3xl">
                            <div className="text-4xl mb-4">📂</div>
                            <p>No projects showcased yet. Check back soon for our latest work.</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Blog Card / Latest Insights */}
            <section className="py-16 px-6 sm:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="card border-nocturne-purple/30 bg-card-gradient grid md:grid-cols-[1.5fr_1fr] gap-12 items-center p-8 md:p-12 glow-purple">
                        <div>
                            <p className="section-tag mb-4">LATEST INSIGHTS</p>
                            <h2 className="text-3xl md:text-5xl font-black text-nocturne-text mb-6 uppercase leading-tight">
                                Engineering the <span className="gradient-text">Future</span> of Performance
                            </h2>
                            <p className="text-nocturne-muted text-lg mb-8 leading-relaxed max-w-xl">
                                We regularly document our breakthroughs in web architecture, edge delivery, and digital strategy. Join our internal network for the latest syntheses.
                            </p>
                            <Link to="/contact" className="btn-primary inline-block px-10 py-4 text-base">
                                Read Full Article →
                            </Link>
                        </div>
                        <div className="hidden md:block">
                            <div className="aspect-square rounded-2xl bg-nocturne-bg border border-nocturne-border flex items-center justify-center relative overflow-hidden group">
                                <div className="absolute inset-0 bg-hero-glow opacity-30 group-hover:opacity-50 transition-opacity" />
                                <div className="text-8xl relative z-10 transition-transform group-hover:scale-110 duration-500">📖</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer CTA */}
            <section className="py-24 px-6 sm:px-8">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl font-black text-nocturne-text mb-6">
                        Architecting <span className="gradient-text">Your Build</span>
                    </h2>
                    <p className="text-nocturne-muted mb-10 max-w-md mx-auto">
                        Ready to elevate your project with enterprise-grade precision? Let's discuss your technical requirements.
                    </p>
                    <Link to="/contact" className="btn-primary inline-block px-8 py-3.5">
                        Get Started
                    </Link>
                </div>
            </section>
        </div>
    );
}
